import React, { useState } from 'react';
import { destinationProblem, type LinkCreateInput } from '../../services/linksService';
import type { AllowBearriLinkAnswer } from '../../services/dealsService';
import { LABEL_MAX } from '../dealroom/dealLinkModel';

/**
 * Make a Bearri link: the brand's exact URL in, a short link out that
 * forwards to it unchanged and counts clicks anonymously.
 *
 * Many sponsor contracts require the exact link, so the creator must tick
 * that their deal allows a forwarding link (or that the brand said yes on the
 * deal link) before the button works. The brand's answer, when there is one,
 * is shown as context — it never ticks the box for them.
 *
 * The server refuses a link it cannot forward honestly (Amazon, Bearri's own
 * hosts, a host that isn't the deal's brand); its sentence is shown verbatim.
 */
export interface TrackedLinkFormProps {
  /** The deal this link belongs to (the Deal Room presets it). */
  pitchId?: string;
  brandName?: string;
  /** The dashboard's optional deal picker. */
  deals?: Array<{ id: string; brand_name: string }>;
  /** The brand's answer on the deal link to "May the creator use a Bearri tracked link?". */
  brandAnswer?: AllowBearriLinkAnswer | null;
  onCreate: (input: LinkCreateInput) => Promise<unknown>;
  onCancel?: () => void;
  idPrefix?: string;
}

const TrackedLinkForm: React.FC<TrackedLinkFormProps> = ({
  pitchId, brandName, deals, brandAnswer = null, onCreate, onCancel, idPrefix = 'tracked-link',
}) => {
  const [destination, setDestination] = useState('');
  const [label, setLabel] = useState('');
  const [dealId, setDealId] = useState(pitchId || '');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = (k: string) => `${idPrefix}-${k}`;
  const brand = brandName || 'The brand';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!confirmed || busy) return;
    const problem = destinationProblem(destination);
    if (problem) { setError(problem); return; }
    if (label.trim().length > LABEL_MAX) { setError(`A name can be ${LABEL_MAX} characters at most.`); return; }
    setBusy(true);
    setError(null);
    try {
      await onCreate({
        destination_url: destination.trim(),
        ...(label.trim() ? { label: label.trim() } : {}),
        ...(dealId ? { pitch_id: dealId } : {}),
        brand_allows_redirect: true,
      });
      setDestination('');
      setLabel('');
      setConfirmed(false);
    } catch (err) {
      // The server's own sentence (Amazon, Bearri's hosts, the deal's domain), shown as it came.
      setError(err instanceof Error ? err.message : 'That link could not be made.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-3" noValidate aria-label="Make a Bearri link">
      {brandAnswer === 'yes' && (
        <p className="br-small" style={{ color: 'var(--green)' }}>
          {brand} said yes to a Bearri link on your deal link.
        </p>
      )}
      {brandAnswer === 'no' && (
        <p className="br-small" style={{ color: 'var(--amber)' }}>
          {brand} said no to a Bearri link on your deal link — share their exact URL instead.
        </p>
      )}
      <label htmlFor={id('dest')} className="block">
        <span className="br-caption block">The brand’s exact link</span>
        <input id={id('dest')} value={destination} onChange={(e) => { setDestination(e.target.value); setError(null); }}
          inputMode="url" autoComplete="off" spellCheck={false} placeholder="https://brand.com/your-page"
          className="br-line-input br-line-input--sm w-full" />
      </label>
      <div className={deals && deals.length > 0 ? 'grid gap-3 sm:grid-cols-2' : ''}>
        <label htmlFor={id('label')} className="block min-w-0">
          <span className="br-caption block">Name it (optional)</span>
          <input id={id('label')} value={label} onChange={(e) => setLabel(e.target.value)} maxLength={LABEL_MAX}
            placeholder="e.g. Video description"
            className="br-line-input br-line-input--sm w-full" />
        </label>
        {deals && deals.length > 0 && (
          <label htmlFor={id('deal')} className="block min-w-0">
            <span className="br-caption block">Deal (optional)</span>
            <select id={id('deal')} value={dealId} onChange={(e) => setDealId(e.target.value)}
              className="br-field mt-1 block w-full px-2 py-1.5 text-sm">
              <option value="">No deal</option>
              {deals.map((d) => <option key={d.id} value={d.id}>{d.brand_name}</option>)}
            </select>
          </label>
        )}
      </div>
      <label htmlFor={id('ok')} className="flex cursor-pointer items-start gap-3">
        <input id={id('ok')} type="checkbox" className="br-check mt-0.5" checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)} />
        <span className="br-small" style={{ color: 'var(--ink)' }}>
          My deal allows a forwarding link, or the brand said yes on the deal link
        </span>
      </label>
      {error && (
        <div role="alert" className="br-note br-note--amber br-note--straight br-note--wide" style={{ marginBottom: 0 }}>
          <p className="min-w-0 flex-1">{error}</p>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" disabled={!confirmed || busy} className="br-slab br-slab--blue br-slab--sm">
          {busy ? 'Making it…' : 'Make my Bearri link'}
        </button>
        {onCancel && <button type="button" onClick={onCancel} className="br-slab br-slab--white br-slab--sm">Cancel</button>}
      </div>
      <p className="br-caption">
        Bearri forwards to this exact URL — it adds nothing, removes nothing and sets no cookie.
        Amazon links can’t be used: Amazon voids commissions on forwarded links.
      </p>
    </form>
  );
};

export default TrackedLinkForm;
